import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  getAiActionErrorMessageKey,
  getAiActionPrompt,
  AiActionError,
} from "@/features/ai/aiErrors";
import {
  buildProjectAiChatMessages,
  streamProjectAiChat as streamProjectAiChatClient,
} from "@/features/ai/aiChatClient";
import { mapAiGuardOutcome } from "@/features/ai/aiMappers";
import type { AiChatExchangeView, AiChatSidebarState } from "@/features/ai/ai.types";

export interface ProjectAiStoreDependencies {
  streamProjectAiChat: typeof streamProjectAiChatClient;
}

export interface ProjectAiStoreState extends AiChatSidebarState {
  activeChapterId: string | null;
  clearChat: () => void;
  hydrateProjectContext: (projectId: string, projectTitle: string) => void;
  projectId: string | null;
  pushExchange: (exchange: AiChatExchangeView) => void;
  requestProjectAiChat: (question: string) => Promise<void>;
  resetProjectContext: () => void;
  setActiveChapterContext: (activeChapterId: string | null, activeChapterLabel: string | null) => void;
  setLoreSummary: (summary: string) => void;
  toggleOpen: (open: boolean) => void;
}

const defaultDependencies: ProjectAiStoreDependencies = {
  streamProjectAiChat: streamProjectAiChatClient,
};

export function createProjectAiStore(
  deps: ProjectAiStoreDependencies = defaultDependencies,
) {
  return createStore<ProjectAiStoreState>((set, get) => ({
    activeChapterId: null,
    activeChapterLabel: null,
    creditsRemaining: null,
    errorMessage: null,
    exchanges: [],
    guardOutcome: null,
    loreSummary: "",
    open: false,
    projectId: null,
    projectTitle: "",
    prompt: null,
    requestStatus: "idle",

    hydrateProjectContext: (projectId, projectTitle) => {
      if (get().projectId === projectId) {
        set({ projectTitle });
        return;
      }

      set({
        activeChapterId: null,
        activeChapterLabel: null,
        creditsRemaining: null,
        errorMessage: null,
        exchanges: [],
        guardOutcome: null,
        loreSummary: "",
        open: false,
        projectId,
        projectTitle,
        prompt: null,
        requestStatus: "idle",
      });
    },

    resetProjectContext: () => {
      set({
        activeChapterId: null,
        activeChapterLabel: null,
        creditsRemaining: null,
        errorMessage: null,
        exchanges: [],
        guardOutcome: null,
        loreSummary: "",
        open: false,
        projectId: null,
        projectTitle: "",
        prompt: null,
        requestStatus: "idle",
      });
    },

    toggleOpen: (open) => {
      set({ open });
    },

    setActiveChapterContext: (activeChapterId, activeChapterLabel) => {
      set({
        activeChapterId,
        activeChapterLabel,
      });
    },

    setLoreSummary: (loreSummary) => {
      set({ loreSummary });
    },

    requestProjectAiChat: async (question) => {
      const { activeChapterId, exchanges, projectId } = get();

      if (!projectId) {
        throw new AiActionError("access-denied", "PROJECT_ACCESS_DENIED");
      }

      const exchangeId = `chat-${Date.now()}`;
      const baseExchange: AiChatExchangeView = {
        answer: "",
        citations: [],
        id: exchangeId,
        isStreaming: true,
        question,
      };

      set({
        errorMessage: null,
        guardOutcome: null,
        prompt: null,
        requestStatus: "loading",
        exchanges: [...exchanges, baseExchange],
      });

      try {
        const result = await deps.streamProjectAiChat(
          {
            activeChapterId,
            messages: buildProjectAiChatMessages(exchanges, question),
            projectId,
          },
          {
            onTextDelta: (delta) => {
              set((state) => ({
                exchanges: state.exchanges.map((exchange) =>
                  exchange.id === exchangeId
                    ? {
                        ...exchange,
                        answer: `${exchange.answer}${delta}`,
                      }
                    : exchange
                ),
              }));
            },
          },
        );

        set((state) => ({
          creditsRemaining:
            typeof result.creditsRemaining === "number"
              ? result.creditsRemaining
              : state.creditsRemaining,
          exchanges: state.exchanges.map((exchange) =>
            exchange.id === exchangeId
              ? {
                  ...exchange,
                  answer: result.answer,
                  citations: result.citations,
                  isStreaming: false,
                }
              : exchange
          ),
          guardOutcome: result.guardOutcome,
          requestStatus: "ready",
        }));
      } catch (error) {
        set((state) => ({
          creditsRemaining:
            error instanceof AiActionError && typeof error.currentBalance === "number"
              ? error.currentBalance
              : state.creditsRemaining,
          errorMessage:
            error instanceof AiActionError
              ? getAiActionErrorMessageKey("chat", error)
              : "story.ai.chat.errors.failed",
          exchanges: state.exchanges.filter((exchange) => exchange.id !== exchangeId),
          guardOutcome:
            error instanceof AiActionError
              ? mapAiGuardOutcome({
                  cacheStatus: error.cacheStatus,
                  code:
                    error.code === "rate-limit-reached"
                      ? "AI_RATE_LIMIT_REACHED"
                      : error.code === "budget-restricted"
                        ? "AI_BUDGET_RESTRICTED"
                        : error.code === "service-busy"
                          ? "AI_SERVICE_BUSY"
                          : undefined,
                  creditsSafe: error.creditsSafe,
                  feature: "chat",
                  resetAt: error.resetAt,
                  retryAfterSeconds: error.retryAfterSeconds,
                  truncated: error.truncated,
                })
              : null,
          prompt: error instanceof AiActionError ? getAiActionPrompt(error) : null,
          requestStatus: "error",
        }));
        throw error;
      }
    },

    pushExchange: (exchange) => {
      set((state) => ({
        exchanges: [...state.exchanges, exchange],
      }));
    },

    clearChat: () => {
      set({
        errorMessage: null,
        exchanges: [],
        guardOutcome: null,
        prompt: null,
        requestStatus: "idle",
      });
    },
  }));
}

export const projectAiStore = createProjectAiStore();

export function useProjectAiStore<T>(selector: (state: ProjectAiStoreState) => T) {
  return useStore(projectAiStore, selector);
}
